import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Popper,
  ClickAwayListener,
  Box,
  IconButton,
  Grow,
  Paper,
  CircularProgress,
} from '@material-ui/core';
import { TextField } from '@ccm-office/ccm-ui';
import { useDebounce } from 'use-debounce';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ClearIcon from '@material-ui/icons/Clear';
import Highlighter from 'react-highlight-words';

import { ITreeItem } from 'types';

import HierarchyTreeView from 'components/HierarchyDropdownSingle/HierarchyTreeView';
import { getNodeFromTree } from 'utils/object';
import useStyles from './style';
import { useTreeSearch } from 'hooks/useTreeSearch';
import { IHierarchyDropdownSingle } from './types';
import { useTranslation } from 'react-i18next';

const HierarchyDropdownSingle: FC<IHierarchyDropdownSingle> = ({
  options,
  label,
  placeholder,
  value = '',
  name,
  error,
  disabled,
  helperText,
  loading,
  onChange = () => {},
  onBlur = () => {},
  onClose = () => {},
  defaultExpanded = [],
  required = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLDivElement>(null);
  const [showTree, setShowTree] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string>(value);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  const classes = useStyles({ showTree });

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const selectedNode = useMemo(() => {
    if (selectedValue) {
      return getNodeFromTree<ITreeItem>(options, selectedValue, 'value');
    }
    return {};
  }, [options, selectedValue]);

  const { node: { name: selectedName = '' } = {}, nodeParents = [] } = selectedNode || {};
  const { nodes: filteredOptions, expandIDS } = useTreeSearch(
    options,
    'name',
    'value',
    debouncedSearchTerm
  );

  const handleInputFocus = () => {
    if (!showTree) setShowTree(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleIconClick = () => {
    if (!showTree && inputRef.current && inputRef.current.focus) {
      inputRef.current.focus();
    } else {
      setShowTree(false);
    }
  };

  const handleClearClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedValue('');
    onChange('');
  };

  const handleTreeSelection = useCallback(
    (value: string) => {
      setSelectedValue(value);
      onChange(value);
      setSearchTerm('');
      setShowTree(false);
    },
    [onChange]
  );

  const handleClose = () => {
    if (showTree) {
      setShowTree(false);
      setSearchTerm('');
      onClose();
    }
  };

  const getInputWith = () => {
    if (inputRef.current) {
      const width = inputRef.current.offsetWidth;
      return { width };
    }

    return { display: 'none' };
  };

  const expandedNodes = useMemo(
    () => (selectedValue ? [...(defaultExpanded || []), ...nodeParents] : defaultExpanded),
    [defaultExpanded, nodeParents, selectedValue]
  );

  const renderLabel = useCallback(
    (node: ITreeItem) => {
      return (
        <>
          {searchTerm ? (
            <Highlighter
              highlightClassName="bolded-word"
              searchWords={[searchTerm]}
              textToHighlight={node.name}
              autoEscape={true}
            />
          ) : (
            node.name
          )}
        </>
      );
    },
    [searchTerm]
  );

  const inputLabelProps = selectedValue || showTree ? { shrink: true } : {};
  const popperModifiers = {
    flip: {
      enabled: false,
    },
    preventOverflow: {
      enabled: true,
      boundariesElement: 'window',
    },
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div data-testid={`hierarchy-picker-${name}`}>
        <TextField
          autoComplete="off"
          disabled={disabled}
          name={name}
          label={label}
          value={showTree ? searchTerm : selectedName}
          fullWidth
          placeholder={placeholder}
          className="hierarchy-text-field"
          InputLabelProps={inputLabelProps}
          inputRef={inputRef}
          required={required}
          InputProps={{
            endAdornment: (
              <>
                {loading ? (
                  <div>
                    <CircularProgress color="inherit" size={15} />
                  </div>
                ) : null}
                {selectedValue && !loading && !disabled && (
                  <IconButton
                    onClick={handleClearClick}
                    title={t('title.clear')}
                    size="small"
                    disabled={disabled}
                    className={classes.clearIcon}
                    data-testid="hierarchyDropdownSingle-clear-button"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  onClick={handleIconClick}
                  disabled={disabled}
                  size="small"
                  data-testid="hierarchyDropdownSingle-expand-button"
                >
                  {showTree ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </>
            ),
          }}
          onBlur={onBlur}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          variant="outlined"
          error={error}
          helperText={helperText}
        />
        {showTree && (
          <Popper
            anchorEl={inputRef.current}
            open
            placement="bottom-start"
            transition
            className={classes.popper}
            modifiers={popperModifiers}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'center top' : 'center bottom',
                }}
              >
                <Paper elevation={3} className={classes.paper} style={{ ...getInputWith() }}>
                  <Box className={classes.treeContainer}>
                    <HierarchyTreeView
                      renderLabel={renderLabel}
                      options={filteredOptions}
                      onChange={handleTreeSelection}
                      value={selectedValue}
                      expandedNodes={[...expandedNodes, ...expandIDS]}
                    />
                  </Box>
                </Paper>
              </Grow>
            )}
          </Popper>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default HierarchyDropdownSingle;
